import type { StepRole, TaskStatus, UserOption } from '@shared/api/types';
import type { Tone } from '@shared/components/base';

import { DASHBOARD_TEXT } from '../constants';

export const ROLE_LABEL: Record<StepRole, string> = {
  WAIT: 'Awaiting client',
  PREP: 'Preparer',
  REV: 'Reviewer',
  ADMIN: 'Admin',
  FILE: 'E-filing',
};

export const TEAM_ROLES: StepRole[] = ['PREP', 'REV', 'ADMIN', 'FILE'];

export const STATUS_TONE: Record<TaskStatus, Tone> = {
  overdue: 'crit',
  due7: 'warn',
  due30: 'info',
  upcoming: 'idle',
  filed: 'accent',
  complete: 'good',
};

export const STATUS_LABEL: Record<TaskStatus, string> = {
  overdue: 'Overdue',
  due7: 'Due in 7 days',
  due30: 'Due in 30 days',
  upcoming: 'Upcoming',
  filed: 'Filed · to archive',
  complete: 'Complete',
};

/** Each service's chip colour on the board (red is left to overdue); others fall back to grey. */
const SERVICE_CHIP: Record<string, string> = {
  return: 'bg-accent-soft text-accent',
  franchise: 'bg-warn-soft text-warn',
  sales: 'bg-info-soft text-info',
  estimates: 'bg-good-soft text-good',
  books: 'bg-idle-soft text-idle',
};

export function serviceChipClass(key: string): string {
  return SERVICE_CHIP[key] ?? 'bg-idle-soft text-idle';
}

/** Services that are worked by one owner: no step band, no step column (prototype). */
export const SINGLE_OWNER_KEYS = new Set(['franchise', 'sales', 'estimates']);

export type ColumnKey =
  'code' | 'client' | 'service' | 'due' | 'month' | 'filingDate' | 'basis' | 'step' | 'with';
export const BOARD_COLUMNS: { key: ColumnKey; label: string; required?: boolean }[] = [
  { key: 'code', label: DASHBOARD_TEXT.columns.code, required: true },
  { key: 'client', label: DASHBOARD_TEXT.columns.client, required: true },
  { key: 'service', label: DASHBOARD_TEXT.columns.service },
  // Month: only while the Service filter is Sales Tax Return; Filing Date also Franchise.
  { key: 'month', label: DASHBOARD_TEXT.columns.month },
  { key: 'due', label: DASHBOARD_TEXT.columns.due },
  { key: 'filingDate', label: DASHBOARD_TEXT.columns.filingDate },
  // Shown only while the Service filter is Estimated Tax.
  { key: 'basis', label: DASHBOARD_TEXT.columns.basis },
  { key: 'step', label: DASHBOARD_TEXT.columns.step },
  { key: 'with', label: DASHBOARD_TEXT.columns.with },
];
/** Saved with each choice: the column list it was made with ("v2": Month / Filing Date,
 *  "v3": Basis Period). Columns added since an older saved choice are shown (ticked), so
 *  they don't vanish for anyone. */
export const COLUMNS_VERSION = 'v3';
const ADDED_IN: Partial<Record<ColumnKey, number>> = { month: 2, filingDate: 2, basis: 3 };
const savedVersion = (saved: string[]) =>
  Math.max(1, ...saved.map((k) => (/^v(\d+)$/.exec(k) ? Number(k.slice(1)) : 1)));
const ALL_KEYS = BOARD_COLUMNS.map((c) => c.key);
/** One choice for the firm-wide Dashboard and My Desk, saved per user on the server. */
export const BOARD_COLUMNS_PREF = 'cols:board';
/** Where this browser kept the choice before it moved to the server. */
export const BOARD_COLUMNS_LEGACY_KEY = 'cd-board-cols';

/** The saved columns that still exist, or every column when nothing usable is saved. */
export function boardColumns(saved: string[] | null): ColumnKey[] {
  const keys = (saved ?? []).filter((k): k is ColumnKey => ALL_KEYS.includes(k as ColumnKey));
  if (!keys.length) return ALL_KEYS;
  const version = savedVersion(saved ?? []);
  return ALL_KEYS.filter((k) => keys.includes(k) || version < (ADDED_IN[k] ?? 0));
}

/** People who can be given work: active and with a desk. */
export function assignable(users: readonly UserOption[] | undefined): UserOption[] {
  return (users ?? []).filter((u) => u.isActive && u.hasDesk);
}
